import { displayDigits } from '../../utils/displayDigits';
import { ActionSymbol } from '../../components/ui/ActionSymbol';
import { ActionIconButton } from '../../components/ui/ActionButton';
import React from 'react';
import { useAnnualPlanTranslation } from '../../hooks/useAnnualPlanTranslation';
import { NmButton } from '../../components/NmButton';
import { formatWeekOfYearFromISO, formatWeekStartFromISO } from '../../i18n/formatters';
import { ATP_PHASES } from './constants';
import { isRecoveryWeek, weekStartISOForPhaseWeek } from './helpers';

interface PhaseTssSheetProps {
  phaseKey: string;
  recoveryCycle?: 3 | 4;
  phaseWeekCounts: Record<string, number>;
  weeklyTSS: Record<string, number[]>;
  planStartISO: string;
  onAdjustTss: (phaseKey: string, weekIdx: number, delta: number) => void;
  onClose: () => void;
}

/** Bottom sheet for per-week TSS editing within a phase (wizard step 4). */
export function PhaseTssSheet({
  phaseKey,
  recoveryCycle = 4,
  phaseWeekCounts,
  weeklyTSS,
  planStartISO,
  onAdjustTss,
  onClose,
}: PhaseTssSheetProps) {
  const { t } = useAnnualPlanTranslation();
  const ph = ATP_PHASES.find(p => p.key === phaseKey);
  if (!ph) return null;

  const weeks = (weeklyTSS[phaseKey] ?? []).slice(0, phaseWeekCounts[phaseKey] ?? 0);
  const phTotal = weeks.reduce((a, b) => a + b, 0);
  const phAvg = weeks.length ? Math.round(phTotal / weeks.length) : 0;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div className="modal-surface atp-tss-sheet" style={S.sheet} onClick={e => e.stopPropagation()}>
        <div style={S.handle} />
        <div style={S.header}>
          <span style={{ ...S.phaseDot, background: ph.color }} />
          <div style={{ flex: 1 }}>
            <div style={S.phName}>{t(`annualPlan.phases.${ph.key}`)}</div>
            <div style={S.phMeta}>
              {t('annualPlan.program.sheetHeaderStats', { total: phTotal.toLocaleString(), avg: phAvg })}
            </div>
          </div>
          <ActionIconButton
            type="button"
            className="atp-tss-close"
            onClick={onClose}
            label={t('common.close')}
            icon={<ActionSymbol name="close" />}
          />
        </div>

        <div className="atp-tss-body" style={S.body}>
          {weeks.map((tss, i) => {
            const isRec = isRecoveryWeek(phaseKey, i, recoveryCycle);
            const weekStartISO = weekStartISOForPhaseWeek(
              phaseKey,
              i,
              phaseWeekCounts,
              planStartISO,
            );
            return (
              <div key={i} style={{ ...S.weekRow, opacity: isRec ? 0.9 : 1 }}>
                <div style={S.weekDateTop}>{displayDigits(formatWeekStartFromISO(weekStartISO))}</div>
                <div className="atp-tss-controls" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  <span style={S.wkNum}>{displayDigits(formatWeekOfYearFromISO(weekStartISO))}</span>
                  <span
                    style={{
                      ...S.badge,
                      background: isRec ? 'rgba(142,148,162,0.18)' : `${ph.color}24`,
                      color: isRec ? 'var(--fg-3)' : ph.color,
                    }}
                  >
                    {isRec
                      ? t('annualPlan.program.weekRecovery')
                      : t('annualPlan.program.weekTraining')}
                  </span>
                  <input
                    aria-label={`${displayDigits(formatWeekStartFromISO(weekStartISO))} · ${t('annualPlan.common.tssPerWeek')}`}
                    type="range"
                    min={0}
                    max={3000}
                    step={1}
                    value={tss}
                    onChange={e => {
                      const next = parseInt(e.target.value, 10);
                      onAdjustTss(phaseKey, i, next - tss);
                    }}
                    style={{ flex: 1, accentColor: ph.color }}
                  />
                  <span style={S.tssVal}>{displayDigits(tss)}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="atp-tss-footer" style={S.footer}>
          <div style={S.footerStats}>
            <div className="atp-tss-stat" style={S.statBox}>
              <div style={S.statVal}>{displayDigits(phTotal.toLocaleString())}</div>
              <div style={S.statKey}>{t('annualPlan.program.totalTss')}</div>
            </div>
            <div className="atp-tss-stat" style={S.statBox}>
              <div style={S.statVal}>{displayDigits(phAvg)}</div>
              <div style={S.statKey}>{t('annualPlan.program.averageTss')}</div>
            </div>
          </div>
          <NmButton variant="primary" onClick={onClose} style={{ width: '100%' }}>
            {t('annualPlan.program.applyChanges')}
          </NmButton>
        </div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 4000,
    background: 'rgba(0,0,0,0.45)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '85vh',
    background: 'var(--surface-0)',
    borderRadius: 'var(--r-4) var(--r-4) 0 0',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.25)',
    border: '1px solid var(--surface-sunken)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    background: 'var(--surface-sunken)',
    alignSelf: 'center',
    marginTop: 10,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 20px',
    borderBottom: '1px solid var(--surface-sunken)',
  },
  phaseDot: { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },
  phName: { fontSize: 15, fontWeight: 700, color: 'var(--fg-1)', fontFamily: 'var(--font-display)' },
  phMeta: { fontSize: 12, color: 'var(--fg-3)', marginTop: 2 },
  closeBtn: {
    background: 'var(--surface-1)',
    border: 'none',
    borderRadius: 8,
    width: 28,
    height: 28,
    cursor: 'pointer',
    color: 'var(--fg-3)',
    boxShadow: 'var(--nm-flat)',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  weekRow: {
    padding: '10px 20px',
    borderBottom: '1px solid var(--surface-sunken)',
  },
  weekDateTop: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--fg-2)',
  },
  wkNum: {
    width: 72,
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--fg-3)',
    fontFamily: 'var(--font-mono)',
    flexShrink: 0,
  },
  badge: {
    fontSize: 9,
    fontWeight: 800,
    padding: '2px 6px',
    borderRadius: 4,
    width: 52,
    textAlign: 'center',
    letterSpacing: '0.04em',
    flexShrink: 0,
  },
  tssVal: {
    width: 44,
    textAlign: 'right',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    color: 'var(--fg-1)',
    flexShrink: 0,
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid var(--surface-sunken)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  footerStats: { display: 'flex', gap: 10 },
  statBox: {
    flex: 1,
    textAlign: 'center',
    padding: '10px 8px',
    borderRadius: 'var(--r-2)',
    background: 'var(--surface-sunken)',
  },
  statVal: { fontSize: 16, fontWeight: 700, color: 'var(--fg-1)', fontFamily: 'var(--font-mono)' },
  statKey: {
    fontSize: 9,
    color: 'var(--fg-4)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginTop: 2,
  },
};
